import { useCallback, useMemo, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { mapMessagesFromServiceToClient } from './convertMessages';
import { useConsentClientTool } from './useConsentClientTool';
import { useClientSideKnowledgeTool } from './useClientSideKnowledgeTool';
import { useScrapePageTool } from './useScrapePageTool';
import { useInitConversation } from './hooks/useInitConversation';
import { useSendUserMessage } from './hooks/useSendUserMessage';
import { useSendAssistantMessage } from './hooks/useSendAssistantMessage';
import { useLoadMoreMessages } from './hooks/useLoadMoreMessages';
import { useRefetchMessages } from './hooks/useRefetchMessages';
import { LogLevel } from '@wix/ambassador-genie-chat-service-v1-no-entity/types';
import { useCreateUserMessage } from './hooks/useCreateUserMessage';
import { getClearRequestId } from '../../../utils/wix/getClearRequestId';
import { insertOrUpdateMessage } from './insertOrUpdateMessage';
const LOAD_MORE_LIMIT = 50;
const ATTACHMENT_ONLY_MESSAGE = '<HIDDEN>See attachments</HIDDEN>';
const safeError = error => error instanceof Error ? error : new Error(JSON.stringify(error));
export const useLiveChat = _ref => {
  let {
    httpClient,
    reference,
    toolRegistry,
    enableLogging,
    enablePolling,
    setLoadingsCount,
    pageUrl,
    pageHtml,
    pageBlob,
    onError
  } = _ref;
  const [conversationIdState, setConversationIdState] = useState();
  const pagingMetadataRef = useRef();
  const conversationIdRef = useRef();
  const setConversationId = useCallback(id => {
    setConversationIdState(id);
    conversationIdRef.current = id;
  }, []);
  const [requests, setRequests] = useState([]);
  const [messages, setMessages] = useState([]);
  const chatManagerLoadMoreMessages = useLoadMoreMessages({
    httpClient
  });
  const chatManagerRefetchMessages = useRefetchMessages({
    httpClient
  });
  const loadMoreMessages = useCallback(async () => {
    const res = await chatManagerLoadMoreMessages({
      limit: LOAD_MORE_LIMIT,
      pagingMetadata: pagingMetadataRef.current,
      conversationId: conversationIdRef.current
    });
    if (!res) {
      return;
    }
    pagingMetadataRef.current = res == null ? void 0 : res.pagingMetadata;
    const receivedMessages = mapMessagesFromServiceToClient(res.messages).map(message => ({
      ...message,
      _conversationMessageId: message.id
    }));
    setMessages(prev => receivedMessages.reduce((acc, message) => insertOrUpdateMessage(acc, message), prev));
  }, [chatManagerLoadMoreMessages]);
  const refetchMessages = useCallback(async () => {
    const res = await chatManagerRefetchMessages({
      limit: LOAD_MORE_LIMIT,
      conversationId: conversationIdRef.current
    });
    if (!res) {
      return;
    }
    const receivedMessages = mapMessagesFromServiceToClient(res.messages).map(message => ({
      ...message,
      _conversationMessageId: message.id
    }));
    setMessages(prev => receivedMessages.reduce((acc, message) => insertOrUpdateMessage(acc, message), prev));
  }, [chatManagerRefetchMessages]);
  const {
    consentTool,
    consentMessage
  } = useConsentClientTool({
    setLoadingsCount
  });
  const {
    getClientSideKnowledge
  } = useClientSideKnowledgeTool({
    pageUrl,
    pageHtml,
    pageBlob
  });
  const {
    getUrlContents
  } = useScrapePageTool();
  const toolRegistryWithBuiltInTools = useMemo(() => {
    const builtInTools = {
      getClientSideKnowledge,
      getUrlContents,
      ...consentTool
    };
    return {
      getTool: async toolName => {
        let tool;
        try {
          tool = await (toolRegistry == null ? void 0 : toolRegistry.getTool(toolName));
        } catch (error) {
          console.warn('Error getting tool from registry', {
            toolName,
            error
          });
        }
        return tool ?? builtInTools[toolName];
      }
    };
  }, [consentTool, getClientSideKnowledge, getUrlContents, toolRegistry]);
  const chatManagerInitConversation = useInitConversation(httpClient, reference);
  const chatManagerSendUserMessage = useSendUserMessage(httpClient, reference, enableLogging, enablePolling, toolRegistryWithBuiltInTools);
  const chatManagerSendAssistantMessage = useSendAssistantMessage(httpClient, reference, enableLogging);
  const createUserMessage = useCreateUserMessage(httpClient);
  const chatManagerStartChat = useCallback(async strategy => {
    const conversation = await chatManagerInitConversation(strategy, LOAD_MORE_LIMIT);
    pagingMetadataRef.current = conversation.pagingMetadata;
    return {
      conversationId: conversation.id,
      conversationMessages: conversation.messages
    };
  }, [chatManagerInitConversation]);
  const sendAssistantMessage = useCallback(async (message, agentName) => {
    try {
      if (!conversationIdRef.current) {
        throw new Error('Chat not initialized. Call startChat first.');
      }
      let tempAssistantMessageId;
      let resolvedMessage;
      if (typeof message === 'function') {
        tempAssistantMessageId = uuid();
        const tempAssistantMessage = {
          id: tempAssistantMessageId,
          content: '',
          role: 'assistant',
          status: 'loading'
        };
        setMessages(prev => insertOrUpdateMessage(prev, tempAssistantMessage));
        resolvedMessage = await message();
      } else {
        resolvedMessage = message;
      }
      const res = await chatManagerSendAssistantMessage(resolvedMessage, conversationIdRef.current, agentName);
      const assistantMessage = {
        id: tempAssistantMessageId,
        _conversationMessageId: res.id,
        createdDate: res.createdDate,
        role: 'assistant',
        content: res.content,
        requestId: res.requestId
      };
      setMessages(prev => insertOrUpdateMessage(prev, assistantMessage));
    } catch (error) {
      console.error(error);
    }
  }, [chatManagerSendAssistantMessage]);
  const handleError = useCallback(_ref2 => {
    let {
      requestId,
      logs,
      error
    } = _ref2;
    if (!onError) {
      return;
    }
    const errorLogs = (logs == null ? void 0 : logs.filter(log => log.level === LogLevel.ERROR)) ?? [];
    if (errorLogs.length > 0) {
      onError({
        requestId,
        logs: errorLogs,
        error
      });
    }
  }, [onError]);
  const sendMessage = useCallback(async (userMessageContent, agentName, sendMessageOptions) => {
    const tempUserMessageId = `temp-${uuid()}`;
    const tempAssistantMessageId = `temp-${uuid()}`;
    try {
      if (!conversationIdRef.current) {
        throw new Error('Chat not initialized. Call startChat first.');
      }
      const attachments = (sendMessageOptions == null ? void 0 : sendMessageOptions.attachments) ?? [];
      const messageReferences = (sendMessageOptions == null ? void 0 : sendMessageOptions.references) ?? [];
      const isAttachmentOnlyMessage = userMessageContent === '' && (attachments == null ? void 0 : attachments.length);
      userMessageContent = isAttachmentOnlyMessage ? ATTACHMENT_ONLY_MESSAGE : userMessageContent;
      const tempUserMessage = {
        id: tempUserMessageId,
        role: 'user',
        content: userMessageContent,
        attachments,
        references: messageReferences
      };
      const tempAssistantMessage = {
        id: tempAssistantMessageId,
        content: '',
        role: 'assistant',
        status: 'loading'
      };
      setMessages(prev => [...prev, tempUserMessage, tempAssistantMessage]);
      const userMessage = await createUserMessage({
        conversationId: conversationIdRef.current,
        userMessageContent,
        attachments,
        messageReferences
      });
      if (!userMessage) {
        throw new Error('Failed to create user message');
      }
      setMessages(prev => prev.map(message => message.id === tempUserMessageId ? {
        ...message,
        _conversationMessageId: userMessage.id,
        createdDate: userMessage.createdDate,
        content: userMessage.content
      } : message.id === tempAssistantMessageId ? {
        ...message,
        userMessageId: userMessage.id
      } : message));
      const sendUserMessageResponse = await chatManagerSendUserMessage({
        message: userMessage,
        conversationId: conversationIdRef.current,
        agentName: agentName ?? null,
        metadata: sendMessageOptions == null ? void 0 : sendMessageOptions.metadata
      });
      const requestId = getClearRequestId(sendUserMessageResponse.requestId);
      setMessages(prev => prev.map(message => message.role === 'assistant' && message.id === tempAssistantMessageId ? {
        ...message,
        _conversationMessageId: sendUserMessageResponse.id,
        createdDate: sendUserMessageResponse.createdDate,
        status: message.status === 'loading' ? 'loaded' : message.status,
        content: sendUserMessageResponse.content,
        requestId,
        userMessageId: userMessage.id
      } : message));
      handleError(sendUserMessageResponse);
    } catch (error) {
      console.error(error);
      const errorMessage = {
        role: 'assistant',
        content: `<<{"widget":"FALLBACK_WIDGET","data":{"errorType":"http","errorCode":"${error.code}"}}>>`
      };
      setMessages(prev => [...prev, errorMessage]);
      handleError({
        error,
        requestId: error == null ? void 0 : error.requestId,
        logs: error == null ? void 0 : error.logs
      });
    }
  }, [chatManagerSendUserMessage, handleError, createUserMessage]);
  const clearChat = useCallback(() => {
    setConversationId(undefined);
    setMessages([]);
  }, [setConversationId]);
  const startChat = useCallback(async startStrategy => {
    try {
      const res = await chatManagerStartChat(startStrategy);
      setRequests([]);
      if (!res.conversationId) {
        throw new Error('conversationId is missing');
      }
      setConversationId(res.conversationId);
      setMessages(mapMessagesFromServiceToClient(res.conversationMessages));
      return res.conversationId;
    } catch (error) {
      throw safeError(error);
    }
  }, [chatManagerStartChat, setConversationId]);
  let finalMessages = messages.map(_ref3 => {
    let {
      _conversationMessageId,
      ...message
    } = _ref3;
    return message;
  });
  if (consentMessage) {
    finalMessages = [...messages, consentMessage];
  }
  return {
    requests,
    messages: finalMessages,
    conversationId: conversationIdState,
    clearChat,
    startChat,
    sendMessage,
    loadMoreMessages,
    refetchMessages,
    sendAssistantMessage
  };
};
//# sourceMappingURL=useLiveChat.js.map